import React, { useContext, useReducer, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Store } from "../Store";
import DeleteModal from "../components/DeleteModal";
import Gorsel from "../components/Gorsel";
import LoadingBox from "../components/LoadingBox";
import { default as config } from "../config.json";
import useAxios from "../hooks/useAxios";
import useTableTypes from "../hooks/useTableTypes";
import { getError } from "../utils";

const reducer = (state, action) => {
  switch (action.type) {
    case "CREATE_REQUEST":
      return { ...state, loadingCreate: true };
    case "CREATE_SUCCESS":
      return {
        ...state,
        loadingCreate: false,
      };
    case "CREATE_FAIL":
      return { ...state, loadingCreate: false };
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false, successDelete: false };

    case "DELETE_RESET":
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function TableTypeListScreen() {
  const axios = useAxios();
  const [{ loadingDelete, loadingCreate }, dispatch] = useReducer(reducer, {
    loadingDelete: false,
    loadingCreate: false,
  });

  const { state } = useContext(Store);
  const { tableTypes = [], compartments = [] } = state;
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  const [guncelleme, setGuncelleme] = useState(false);
  const [showTableTypeEditor, setShowTableTypeEditor] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTableType, setSelectedTableType] = useState(null);

  const [validated, setValidated] = useState(false);
  const [image, setImage] = useState("");
  const [name, setName] = useState("");
  const [tableNumbers, setTableNumbers] = useState([]);
  const [, , setLoadTableTypes] = useTableTypes();

  const createHandler = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    try {
      dispatch({ type: "CREATE_REQUEST" });
      if (!guncelleme) {
        const { data } = await axios.post("/api/tabletypes", {
          name,
          image: image || config.default_image,
          tableNumbers,
        });
      } else {
        const { data } = await axios.put(`/api/tabletypes/${selectedId}`, {
          name,
          image: image || config.default_image,
          tableNumbers,
          _id: selectedId,
        });
      }
      toast.success("Masa türü başarılı bir şekilde kaydedildi!");
      dispatch({ type: "CREATE_SUCCESS" });
      setName("");
      setImage("");
      setTableNumbers([]);
      setLoadTableTypes(true);
      setShowTableTypeEditor(false);
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: "CREATE_FAIL",
      });
    }
  };

  const deleteHandler = async (tableType) => {
    try {
      dispatch({ type: "DELETE_REQUEST" });

      if (tableType.image !== config.default_image)
        try {
          await axios.delete(
            `/api/upload/${tableType._id}?entityName=tabletype`
          );
        } catch (error) {}

      await axios.delete(`/api/tabletypes/${tableType._id}`);
      setLoadTableTypes(true);
      toast.success("Masa tipi başarılı bir şekilde silindi");
      dispatch({ type: "DELETE_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: "DELETE_FAIL",
      });
    }
  };

  const handleClickNewTableType = () => {
    setGuncelleme(false);
    setShowTableTypeEditor(true);
  };

  const handleTableNumberChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) {
      setTableNumbers((prev) => [...prev, value]);
    } else {
      setTableNumbers((prev) => prev.filter((num) => num !== value));
    }
  };

  return (
    <div className="small-container">
      <Helmet>
        <title>Masa Tipleri</title>
      </Helmet>
      {!isTabletOrMobile && (
        <>
          <h1 className="my-3 text-muted">Masa Tipleri</h1>
          <hr className="hr"></hr>
        </>
      )}
      <div className="hstack gap-3 mb-3">
        <div className="ms-auto">
          <div>
            <Button
              type="button"
              onClick={() => handleClickNewTableType()}
              className={"text-primary border-primary"}
              variant="light"
            >
              <i className="fas fa-plus"></i>
              {!isTabletOrMobile && <>&nbsp; Masa Tipi Oluştur</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="list-group">
        {tableTypes
          ?.filter((t) => t.status)
          .map((tableType) => (
            <div
              key={tableType._id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center">
                <Link
                  className="textDecorationNone"
                  to={``}
                  onClick={() => {
                    setShowTableTypeEditor(true);
                    setGuncelleme(true);
                    setSelectedId(tableType._id);
                    setName(tableType.name);
                    setImage(tableType.image);
                    setTableNumbers(tableType.tableNumbers || []);
                  }}
                >
                  <img
                    src={tableType.image}
                    alt={tableType.name}
                    className="me-3"
                    style={{ width: "50px", height: "50px" }}
                  />
                  <span>{tableType.name}</span>
                </Link>
              </div>
              <div>
                {loadingDelete && selectedTableType?._id === tableType._id ? (
                  <LoadingBox />
                ) : (
                  <Button
                    type="button"
                    className="border-danger text-danger"
                    variant="light"
                    onClick={() => {
                      setSelectedTableType(tableType);
                      setShowConfirmModal(true);
                    }}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </Button>
                )}
              </div>
            </div>
          ))}
      </div>

      <Modal
        backdrop="static"
        show={showTableTypeEditor}
        onHide={() => {
          setShowTableTypeEditor(false);
        }}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>
            <span className="text-muted">
              {guncelleme ? "Yeni Masa Tipi" : name}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            noValidate
            validated={validated}
            onSubmit={(e) => {
              e.preventDefault();
              if (e.currentTarget.checkValidity() === false) {
                e.stopPropagation();
                setValidated(true);
                return;
              }
              createHandler(e);
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Masa Tipi Adı</Form.Label>
              <Form.Control
                placeholder="Masa tipi adı"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <Form.Control.Feedback type="invalid">
                Lütfen ad alanını boş bırakmayınız.
              </Form.Control.Feedback>
            </Form.Group>

            <Gorsel
              image={image}
              setImage={setImage}
              entityId={selectedId}
              entityName={"tabletype"}
            ></Gorsel>

            <Form.Group className="mb-3">
              <Form.Label>Masa Numaraları</Form.Label>
              <div
                className="d-grid overflow-auto"
                style={{
                  maxHeight: "200px",
                  gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                  gap: "10px",
                }}
              >
                {[
                  ...Array(
                    compartments && compartments.length > 0
                      ? compartments[compartments.length - 1].endNum
                      : 40
                  ),
                ].map((_, index) => (
                  <Form.Check
                    key={index + 1}
                    type="checkbox"
                    label={`Masa ${index + 1}`}
                    value={index + 1}
                    checked={tableNumbers.includes(index + 1)}
                    onChange={handleTableNumberChange}
                    className="me-2"
                    disabled={tableTypes
                      .filter((tt) => tt.status)
                      .some(
                        (tableType) =>
                          tableType.tableNumbers?.includes(index + 1) &&
                          tableType._id !== selectedId
                      )}
                  />
                ))}
              </div>
            </Form.Group>

            <Button
              variant="outline-primary"
              size="lg"
              type="submit"
              style={{ width: "50%" }}
              className="me-2"
            >
              Kaydet
            </Button>

            <Button
              variant="outline-danger"
              size="lg"
              onClick={() => setShowTableTypeEditor(false)}
            >
              Kapat
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <DeleteModal
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        deleteHandler={deleteHandler}
        objectToDelete={selectedTableType}
        title={"Masa Türünü Sil"}
        message={"Silmek istediğinize emin misiniz?"}
      />
    </div>
  );
}
